import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, Lock, TrendingUp, Wallet, ShieldCheck, Loader2, XCircle } from "lucide-react";
import { authAxios } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";

const perks = [
  { icon: TrendingUp, title: "Highest-profit exclusive links", body: "Access affiliate offers we don't publish to the free directory — the ones that pay the most per click." },
  { icon: Wallet, title: "Your own tracked share link", body: "Every exclusive link becomes /go/@your-handle — earn on every click and signup you send." },
  { icon: ShieldCheck, title: "Cancel anytime, no lock-in", body: "One click cancels in your dashboard. You keep access until the end of the paid period." },
  { icon: Sparkles, title: "First access to new drops", body: "New high-converting partners land in the Members Club first — free users get them 30 days later, if at all." },
];

const faqs = [
  { q: "What do I actually get?", a: "Access to Social Flats' exclusive high-profit affiliate directory + a personal /go/ tracked link on every exclusive program that pays you when your traffic converts." },
  { q: "Can I cancel any time?", a: "Yes. One click in your dashboard. You keep access until your paid period ends — no partial refunds, no games." },
  { q: "Is this really $1.99/month?", a: "Yes. Less than a coffee. We keep it low because our real revenue comes from the same affiliate commissions you're earning — we win when you win." },
  { q: "Is my payment secure?", a: "Payments run through Stripe — the same processor used by Amazon, Google, and Shopify. Card details never touch our servers." },
];

export default function MembershipPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    authAxios.get("/membership/status")
      .then((r) => setStatus(r.data))
      .catch((err) => console.error("membership status:", err));
  }, [user]);

  const checkout = async () => {
    if (!user) {
      navigate("/login?next=/membership");
      return;
    }
    setBusy(true);
    try {
      const r = await authAxios.post("/membership/checkout", { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel membership at the end of the current billing period? You keep access until then.")) return;
    setBusy(true);
    try {
      await authAxios.post("/membership/cancel");
      toast.success("Membership will end at the current period's end.");
      await refreshUser();
      const r = await authAxios.get("/membership/status");
      setStatus(r.data);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Could not cancel");
    } finally {
      setBusy(false);
    }
  };

  const isPaid = status?.is_paid;
  const endsAt = status?.current_period_end ? new Date(status.current_period_end) : null;

  return (
    <main className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-16 sm:py-24">
        <div className="text-center max-w-3xl mx-auto">
          <p className="inline-flex items-center gap-2 text-xs tracking-[0.2em] font-bold text-emerald-600 uppercase mb-4">
            <Sparkles className="w-4 h-4" /> Members Club
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-slate-900 leading-tight">
            Unlock the affiliate links that <span className="text-emerald-600">actually make money</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            $1.99/month gets you into the room where the highest-paying affiliate programs live. Plus a personal tracked share link on every one — you earn every time someone signs up through you.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-8 items-start">
          {/* Pricing card */}
          <div data-testid="membership-pricing-card" className="bg-white border-2 border-emerald-500 rounded-2xl p-8 shadow-xl shadow-emerald-500/10 relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-emerald-500 text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl">MOST POPULAR</div>
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Members Club</p>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="text-6xl font-bold text-slate-900">$1.99</span>
              <span className="text-slate-500">/month</span>
            </div>
            <p className="mt-2 text-sm text-slate-500">Billed monthly. Cancel anytime.</p>

            <div className="mt-6 space-y-3">
              {perks.map((p) => (
                <div key={p.title} className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">{p.title}</p>
                    <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
                  </div>
                </div>
              ))}
            </div>

            {isPaid ? (
              <div className="mt-8 space-y-3">
                <div data-testid="membership-active-badge" className="w-full py-4 rounded-full bg-emerald-50 border-2 border-emerald-200 text-emerald-800 font-bold text-center text-sm">
                  <ShieldCheck className="w-4 h-4 inline mr-2" />
                  Your membership is active
                  {endsAt && (
                    <p className="text-xs font-normal text-emerald-700 mt-1">
                      {status?.cancel_at_period_end
                        ? `Access until ${endsAt.toLocaleDateString()}`
                        : `Renews on ${endsAt.toLocaleDateString()}`}
                    </p>
                  )}
                </div>
                {!status?.cancel_at_period_end && (
                  <button
                    data-testid="membership-cancel-button"
                    onClick={cancel}
                    disabled={busy}
                    className="w-full py-3 rounded-full border border-slate-300 text-slate-700 text-sm font-medium hover:border-slate-900 disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : <><XCircle className="w-4 h-4 inline mr-2" /> Cancel membership</>}
                  </button>
                )}
              </div>
            ) : (
              <button
                data-testid="membership-checkout-button"
                onClick={checkout}
                disabled={busy}
                className="mt-8 w-full py-4 rounded-full bg-emerald-600 text-white font-bold hover:bg-emerald-500 disabled:opacity-50 transition-all shadow-lg shadow-emerald-500/30"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : `Join for $1.99/mo →`}
              </button>
            )}
            <p className="mt-3 text-xs text-slate-400 text-center">Payments processed by Stripe. Cancel any time.</p>
          </div>

          {/* Comparison */}
          <div className="bg-white border border-slate-200 rounded-2xl p-8">
            <h3 className="font-heading font-bold text-slate-900 text-xl mb-6">Free vs. Members Club</h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="col-span-1 font-semibold text-slate-700">&nbsp;</div>
              <div className="col-span-1 font-semibold text-slate-500 text-center">Free</div>
              <div className="col-span-1 font-semibold text-emerald-700 text-center">Members</div>

              {[
                ["Public affiliate directory", true, true],
                ["Sign up to affiliate programs", true, true],
                ["Exclusive high-profit links", false, true],
                ["Personal /go/ tracked share links", false, true],
                ["Early access to new programs", false, true],
              ].map(([label, free, paid]) => (
                <div key={label} className="contents">
                  <div className="col-span-1 text-slate-700 py-2 border-t border-slate-100">{label}</div>
                  <div className="col-span-1 text-center py-2 border-t border-slate-100">{free ? <CheckCircle2 className="w-5 h-5 text-slate-400 inline" /> : <Lock className="w-4 h-4 text-slate-300 inline" />}</div>
                  <div className="col-span-1 text-center py-2 border-t border-slate-100">{paid ? <CheckCircle2 className="w-5 h-5 text-emerald-600 inline" /> : <Lock className="w-4 h-4 text-slate-300 inline" />}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h3 className="text-2xl font-heading font-bold text-slate-900 text-center mb-8">Common questions</h3>
          <div className="space-y-4">
            {faqs.map((f) => (
              <details key={f.q} className="bg-white border border-slate-200 rounded-xl p-5 group">
                <summary className="font-semibold text-slate-900 cursor-pointer flex justify-between items-center">
                  {f.q}
                  <span className="text-slate-400 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>

        {!user && (
          <div className="mt-12 text-center text-sm text-slate-500">
            Don&apos;t have an account yet? <Link to="/signup" className="text-emerald-600 font-semibold hover:underline">Sign up free</Link> — then upgrade any time.
          </div>
        )}
      </div>
    </main>
  );
}
